import { useCallback, useEffect, useState } from 'react';

import { EmblaCarouselType } from 'embla-carousel';

type UseProgressBarType = {
    scrollProgress: number;
};

/** @function quantize
 * @description ## Quantization to nearest step
 * `f(x) = ∆ · round(x / ∆)`
 * Reduce precision by mapping a continuous (or high-resolution) sequence into discrete levels (30, 40, 50, ... 100).
 * @param x - input number
 * @param delta - quantization step (Δ).
 * > The smaller the Δ - the smoother the progress bar, the bigger -- the more jumpy.
 *  Too small Δ - can result in performance loss, because insane number of digits will appear after the decimal point
 * @returns a rounded number
 * */
const quantize = (x: number, delta = 0.05) => {
    return Math.round(x / delta) * delta;
};

export const useProgressBar = (emblaApi: EmblaCarouselType | undefined, length: number): UseProgressBarType => {
    const [scrollProgress, setScrollProgress] = useState(0);

    const onScroll = useCallback(
        (embla: EmblaCarouselType) => {
            const progress = Math.max(0, Math.min(1, (embla.scrollProgress() * (length - 1) + 1) / length));
            setScrollProgress(quantize(progress) * 100);
        },
        [length],
    );

    useEffect(() => {
        if (!emblaApi) {
            return;
        }

        onScroll(emblaApi);
        emblaApi.on('reInit', onScroll).on('scroll', onScroll).on('slideFocus', onScroll);
    }, [emblaApi, onScroll]);

    return {
        scrollProgress,
    };
};
